import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Premium full-screen lightbox/modal for gallery items.
 * Props:
 *   items: [{type:'image'|'video', src, title?, tag?}]
 *   index: current index (null = closed)
 *   onClose(): close handler
 *   onNav(nextIndex): change handler
 */
export const Lightbox = ({ items, index, onClose, onNav }) => {
  const isOpen = index !== null && index !== undefined && items && items[index];

  const next = useCallback(() => {
    if (!items) return;
    onNav((index + 1) % items.length);
  }, [items, index, onNav]);

  const prev = useCallback(() => {
    if (!items) return;
    onNav((index - 1 + items.length) % items.length);
  }, [items, index, onNav]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, next, prev]);

  if (!isOpen) return null;
  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-8 py-10 bg-[rgba(5,5,5,0.92)] backdrop-blur-2xl"
      style={{ animation: "fadeUp 280ms cubic-bezier(0.22,1,0.36,1) both" }}
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 sm:top-7 sm:right-7 h-10 w-10 rounded-full border border-[rgba(245,241,232,0.18)] bg-[rgba(14,14,14,0.7)] backdrop-blur flex items-center justify-center text-[#F5F1E8] hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition"
        data-testid="lightbox-close"
        aria-label="Fechar"
      >
        <X size={18} />
      </button>

      {/* Prev */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
          className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-[rgba(245,241,232,0.18)] bg-[rgba(14,14,14,0.7)] backdrop-blur flex items-center justify-center text-[#F5F1E8] hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition"
          aria-label="Anterior"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {/* Next */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-[rgba(245,241,232,0.18)] bg-[rgba(14,14,14,0.7)] backdrop-blur flex items-center justify-center text-[#F5F1E8] hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition"
          aria-label="Próximo"
          data-testid="lightbox-next"
        >
          <ChevronRight size={20} />
        </button>
      )}

      {/* Content */}
      <div
        className="relative max-w-5xl w-full max-h-[88vh] rounded-2xl overflow-hidden border border-[rgba(197,212,181,0.22)] bg-[#0A0A0A] sage-glow-soft"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "fadeUp 420ms cubic-bezier(0.22,1,0.36,1) both" }}
      >
        {item.type === "video" ? (
          <video
            key={item.src}
            src={item.src}
            autoPlay
            controls
            loop
            playsInline
            className="w-full h-full max-h-[88vh] object-contain bg-black"
          />
        ) : (
          <img
            src={item.src}
            alt={item.title || ""}
            className="w-full h-full max-h-[88vh] object-contain bg-black"
          />
        )}

        {(item.title || item.tag) && (
          <div className="absolute left-0 right-0 bottom-0 p-5 sm:p-7 bg-gradient-to-t from-[rgba(5,5,5,0.92)] to-transparent">
            {item.tag && (
              <span className="mono text-[10.5px] tracking-[0.22em] uppercase text-[#C5D4B5]">
                {item.tag}
              </span>
            )}
            {item.title && (
              <p className="mt-1.5 text-[16px] sm:text-[18px] font-medium tracking-tight text-[#F5F1E8]">
                {item.title}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Index indicator */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 mono text-[11px] tracking-[0.22em] uppercase text-[#F5F1E8]/60">
        {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
      </div>
    </div>
  );
};

export default Lightbox;
